import { distinctUntilChanged, interval, map, shareReplay, switchMap } from 'rxjs'
import { fromPromise } from 'rxjs/internal/observable/innerFrom'

const askWriteClipboard = () =>
	navigator.permissions
		.query({ name: 'clipboard-write' as PermissionName })
		.then((result) => result.state === 'granted' || result.state === 'prompt')

const aksReadClipboard = () =>
	navigator.permissions
		.query({ name: 'clipboard-read' as PermissionName })
		.then((result) => result.state === 'granted' || result.state === 'prompt')

export const canReadClipboard = () => {
	if (document.hasFocus()) return aksReadClipboard()

	return new Promise<boolean>((resolve) => {
		const onFocus = () => {
			aksReadClipboard().then(resolve)
			window.removeEventListener('focus', onFocus)
		}
		window.addEventListener('focus', onFocus)
	})
}

export const canWriteClipboard = () => {
	if (document.hasFocus()) return askWriteClipboard()

	return new Promise<boolean>((resolve) => {
		const onFocus = () => {
			askWriteClipboard().then(resolve)
			window.removeEventListener('focus', onFocus)
		}
		window.addEventListener('focus', onFocus)
	})
}

const parseClipbaordData = <T = string>(data: string, key?: string) => {
	if (!key) return data as T

	try {
		const json = JSON.parse(data)
		if (json['_clipboardKey'] && json['_clipboardKey'] === key) {
			return json.data as T
		}
	} catch {
		return undefined
	}

	return undefined
}

export const readClipboard = <T = string>(key?: string) =>
	canReadClipboard().then(async (canRead) => {
		if (!canRead) return undefined
		return parseClipbaordData<T>(await navigator.clipboard.readText(), key)
	})

export function writeClipboard(dataOrKey: string): Promise<void>
export function writeClipboard(dataOrKey: string, data: unknown): Promise<void>
export function writeClipboard(dataOrKey: string, data?: unknown): Promise<void> {
	return canWriteClipboard().then((canWrite) => {
		if (canWrite) {
			if (data) {
				return navigator.clipboard.writeText(JSON.stringify({ _clipboardKey: dataOrKey, data }))
			}

			return navigator.clipboard.writeText(dataOrKey)
		}

		return undefined
	})
}

const clipboardContent$ = interval(1000).pipe(
	switchMap(() =>
		fromPromise(canReadClipboard().then((canRead) => (canRead ? navigator.clipboard.readText() : undefined))),
	),
	distinctUntilChanged(),
	shareReplay(1),
)

export const clipboard$ = <T = string>(dataOrKey?: string) =>
	clipboardContent$.pipe(
		map((data) => (data ? parseClipbaordData<T>(data, dataOrKey) : undefined)),
		distinctUntilChanged(),
		shareReplay(1),
	)
